// Save envelope schema (IMPLEMENTATION §2.9 "Save": header → migrations → zod envelope → strip transient).
// zod/mini (tree-shakable, a few KB) validates the version-agnostic HEADER before migrating, then the current
// ENVELOPE and the top-level shape of the sim state; the state's content is checked by the sim itself
// (canonical JSON = integers only, and the invariants).
// Since M5 zod ships in the runtime bundle for this one purpose; the data compiler and the 13 data-file
// schemas still stay out of it (tests/e2e/m1-data.spec.ts, M5 decisions).
import * as z from 'zod/mini';

export const SAVE_GAME = 'gunmerger';

const Loose = z.record(z.string(), z.unknown());

/** The sim state's top level (its content is checked by the sim: integers only + invariants). */
export const SimShape = z.looseObject({
  v: z.int(),
  tick: z.int(),
  rng: z.int(),
  dropRng: z.int(),
  nextId: z.int(),
  bounds: z.int(),
  floor: z.array(z.unknown()),
  buildings: z.array(z.unknown()),
  wallet: Loose,
  income: Loose,
  progress: Loose,
  transient: Loose,
  ledger: Loose,
});

export const MetaSchema = z.object({
  settings: Loose,
  sessionCount: z.int(),
  uiFlags: Loose,
});

export const EnvelopeSchema = z.object({
  v: z.int(),
  game: z.literal(SAVE_GAME),
  savedAtWallMs: z.number(),
  lastSeenWallMs: z.number(),
  maxWallSeenMs: z.number(),
  defsHash: z.string(),
  sim: SimShape,
  meta: MetaSchema,
});

export type SaveMeta = z.infer<typeof MetaSchema>;

/**
 * The version-agnostic header every save format keeps (checked BEFORE migrations; unknown keys are kept for
 * them). The full current envelope is validated after migrating.
 */
export const HeaderSchema = z.looseObject({
  v: z.int(),
  game: z.literal(SAVE_GAME),
  sim: z.looseObject({ v: z.int() }),
});

function firstIssue(
  prefix: string,
  issues: readonly { path: PropertyKey[]; message: string }[],
): string {
  const first = issues[0];
  const at = first ? first.path.map(String).join('.') : '';
  return `${prefix}: ${at ? `${at}: ` : ''}${first?.message ?? 'invalid'}`;
}

/** Checks the header of a save of any format version. */
export function checkHeader(
  raw: unknown,
): { ok: true; header: z.infer<typeof HeaderSchema> } | { ok: false; reason: string } {
  const r = HeaderSchema.safeParse(raw);
  if (r.success) return { ok: true, header: r.data };
  return { ok: false, reason: firstIssue('envelope', r.error.issues) };
}

/** Validates the envelope; returns the first problem as text on failure. */
export function checkEnvelope(
  raw: unknown,
): { ok: true; env: z.infer<typeof EnvelopeSchema> } | { ok: false; reason: string } {
  const r = EnvelopeSchema.safeParse(raw);
  if (r.success) return { ok: true, env: r.data };
  return { ok: false, reason: firstIssue('envelope', r.error.issues) };
}
